/**
 * Markdown-to-channel format converters.
 *
 * LLMs output standard Markdown. These pure functions convert it to each
 * channel's native formatting before external delivery. The original markdown
 * is stored unchanged — conversion happens at the delivery layer only.
 *
 * Key: process **bold** before *italic* to avoid double-conversion.
 *
 * Channel senders accept only the branded types defined here. A raw string
 * cannot be sent — callers must produce a branded value via a formatter
 * (markdown → native), a plain helper (text → escaped native), or an
 * explicit `as*Unsafe` escape hatch. This makes Telegram HTML injection and
 * similar encoding bugs a compile error, not a runtime audit.
 */

// ---------------------------------------------------------------------------
// Branded types — only constructable via the helpers below
// ---------------------------------------------------------------------------

declare const __channelBrand: unique symbol;

type Brand<Tag extends string> = string & { readonly [__channelBrand]: Tag };

/** WhatsApp message body. Native syntax: *bold* _italic_ ~strike~ ```code```. */
export type WhatsAppText = Brand<'whatsapp'>;
/** Instagram message body. Plain text — no native formatting. */
export type InstagramText = Brand<'instagram'>;
/** Email message body. Fragment HTML (no `<!doctype>` wrapper). */
export type EmailHtml = Brand<'email'>;
/** Telegram message body. HTML subset: `<b>`, `<i>`, `<s>`, `<code>`, `<pre>`, `<a>`. Always sent with `parse_mode: 'HTML'`. */
export type TelegramHtml = Brand<'telegram'>;
/** App channel body. Stored markdown, rendered client-side. */
export type AppText = Brand<'app'>;
/** System channel body. Internal-only. */
export type SystemText = Brand<'system'>;

export type ChannelFormatted = WhatsAppText | InstagramText | EmailHtml | TelegramHtml | AppText | SystemText;

/** Sanitize URL for safe use in HTML href/src attributes — block javascript:, data:, vbscript: */
function sanitizeUrl(url: string): string {
  // Normalize: strip control characters, decode HTML entities (browsers do this before scheme resolution),
  // then check for dangerous protocols. Entity decoding handles both &#NNN; and &#xHH; forms,
  // plus &amp; chains from our own entity escaping step.
  const normalized = url
    .replace(/[\x00-\x1f\x7f]/g, '')
    .replace(/&amp;/gi, '&')
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([\da-f]+);/gi, (_m, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/%([0-9a-f]{2})/gi, (_m, hex) => String.fromCharCode(parseInt(hex, 16)))
    .trim()
    .toLowerCase();
  if (normalized.startsWith('javascript:') || normalized.startsWith('data:') || normalized.startsWith('vbscript:')) {
    return '';
  }
  return url;
}

// ---------------------------------------------------------------------------
// WhatsApp: *bold* _italic_ ~strike~ ```code```
// ---------------------------------------------------------------------------

export function markdownToWhatsApp(text: string): WhatsAppText {
  let result = text;

  // Code blocks: ```lang\ncode\n``` → ```code``` (preserve, WhatsApp supports natively)
  result = result.replace(/```[\w]*\n?([\s\S]*?)```/g, '```$1```');

  // Bold+italic: ***text*** → *_text_*
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '*_$1_*');

  // Bold: **text** → *text* (must come before italic)
  result = result.replace(/\*\*(.+?)\*\*/g, '*$1*');

  // Italic: _text_ stays as _text_ (WhatsApp native)
  // Italic: *text* → _text_ (only single asterisks that aren't part of bold)
  // After bold conversion, remaining single * pairs are italic
  // We need to be careful not to match the bold markers we just created
  // Skip this — single * is already used for bold in WhatsApp. Markdown italic *text*
  // becomes WhatsApp bold, which is an acceptable tradeoff.

  // Strikethrough: ~~text~~ → ~text~
  result = result.replace(/~~(.+?)~~/g, '~$1~');

  // Headers: # Heading → *Heading* (bold as substitute)
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '*$1*');

  // Images: ![alt](url) → alt (url) (must come before links)
  result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1 ($2)');

  // Links: [text](url) → text (url)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');

  return result as WhatsAppText;
}

// ---------------------------------------------------------------------------
// Plain text (Instagram): strip all formatting
// ---------------------------------------------------------------------------

export function markdownToPlainText(text: string): InstagramText {
  let result = text;

  // Code blocks: ```lang\ncode\n``` → code
  result = result.replace(/```[\w]*\n?([\s\S]*?)```/g, '$1');

  // Inline code: `code` → code
  result = result.replace(/`([^`]+)`/g, '$1');

  // Bold+italic: ***text*** → text
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '$1');

  // Bold: **text** or __text__ → text
  result = result.replace(/\*\*(.+?)\*\*/g, '$1');
  result = result.replace(/__(.+?)__/g, '$1');

  // Italic: *text* or _text_ → text
  result = result.replace(/\*(.+?)\*/g, '$1');
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, '$1');

  // Strikethrough: ~~text~~ → text
  result = result.replace(/~~(.+?)~~/g, '$1');

  // Headers: # Heading → Heading
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '$1');

  // Links: [text](url) → text (url)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');

  // Images: ![alt](url) → alt (url)
  result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1 ($2)');

  // Horizontal rules: --- or *** → empty line
  result = result.replace(/^[-*_]{3,}$/gm, '');

  return result as InstagramText;
}

// ---------------------------------------------------------------------------
// HTML (Email): fragment HTML — no <!DOCTYPE> wrapper
// ---------------------------------------------------------------------------

export function markdownToHtml(text: string): EmailHtml {
  // Escape HTML entities first (including " for safe attribute values)
  let result = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Code blocks: extract and replace with placeholders to protect from further processing
  const codeBlocks: string[] = [];
  result = result.replace(/```[\w]*\n?([\s\S]*?)```/g, (_match, code) => {
    codeBlocks.push(`<pre><code>${code}</code></pre>`);
    return `\x00CODE${codeBlocks.length - 1}\x00`;
  });

  // Inline code: `code` → <code>code</code>
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Images: ![alt](url) → <img> (before links to avoid conflict, URL sanitized)
  result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, url) => {
    const safe = sanitizeUrl(url);
    return safe ? `<img src="${safe}" alt="${alt}" style="max-width:100%">` : alt;
  });

  // Links: [text](url) → <a> (URL sanitized)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
    const safe = sanitizeUrl(url);
    return safe ? `<a href="${safe}">${text}</a>` : text;
  });

  // Headers: # Heading → <h1>-<h6>
  result = result.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  result = result.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  result = result.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  result = result.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  result = result.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  result = result.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // Bold+italic: ***text*** → <strong><em>text</em></strong>
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');

  // Bold: **text** → <strong>text</strong>
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic: *text* → <em>text</em>
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Strikethrough: ~~text~~ → <s>text</s>
  result = result.replace(/~~(.+?)~~/g, '<s>$1</s>');

  // Horizontal rules: --- → <hr>
  result = result.replace(/^[-*_]{3,}$/gm, '<hr>');

  // Unordered lists: use UL-specific markers to avoid confusion with ordered lists
  result = result.replace(/^[-*+]\s+(.+)$/gm, '\x00UL-LI\x00$1\x00/UL-LI\x00');
  result = result.replace(/((?:\x00UL-LI\x00.*?\x00\/UL-LI\x00\n?)+)/g, (_m, items) => {
    const lis = items.replace(/\x00UL-LI\x00(.*?)\x00\/UL-LI\x00/g, '<li>$1</li>');
    return `<ul>${lis}</ul>\n`;
  });

  // Ordered lists: use OL-specific markers
  result = result.replace(/^\d+\.\s+(.+)$/gm, '\x00OL-LI\x00$1\x00/OL-LI\x00');
  result = result.replace(/((?:\x00OL-LI\x00.*?\x00\/OL-LI\x00\n?)+)/g, (_m, items) => {
    const lis = items.replace(/\x00OL-LI\x00(.*?)\x00\/OL-LI\x00/g, '<li>$1</li>');
    return `<ol>${lis}</ol>\n`;
  });

  // Paragraphs: double newlines → <br><br>, single newlines → <br>
  result = result.replace(/\n\n/g, '<br><br>');
  result = result.replace(/\n/g, '<br>');

  // Remove spurious <br> after block-level elements (they already create visual breaks)
  result = result.replace(/(<\/(?:ul|ol|h[1-6]|pre|blockquote)>|<hr>)<br>/g, '$1');

  // Restore code blocks (newlines preserved inside <pre>)
  result = result.replace(/\x00CODE(\d+)\x00/g, (_match, idx) => codeBlocks[Number(idx)]);

  return result as EmailHtml;
}

// ---------------------------------------------------------------------------
// Telegram HTML: <b>, <i>, <s>, <code>, <pre>, <a>
// Telegram only supports a subset of HTML tags.
// ---------------------------------------------------------------------------

export function markdownToTelegramHtml(text: string): TelegramHtml {
  // Escape HTML entities first (Telegram requires this, including " for attributes)
  let result = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Code blocks: extract and replace with placeholders to protect from further processing
  const codeBlocks: string[] = [];
  result = result.replace(/```[\w]*\n?([\s\S]*?)```/g, (_match, code) => {
    codeBlocks.push(`<pre>${code}</pre>`);
    return `\x00CODE${codeBlocks.length - 1}\x00`;
  });

  // Inline code: `code` → <code>code</code>
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Links: [text](url) → <a href="url">text</a> (URL sanitized)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
    const safe = sanitizeUrl(url);
    return safe ? `<a href="${safe}">${text}</a>` : text;
  });

  // Images: ![alt](url) → alt (url) (Telegram doesn't support inline images in HTML mode)
  result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1 ($2)');

  // Headers: # Heading → <b>Heading</b> (Telegram has no heading tags)
  result = result.replace(/^#{1,6}\s+(.+)$/gm, '<b>$1</b>');

  // Bold+italic: ***text*** → <b><i>text</i></b>
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<b><i>$1</i></b>');

  // Bold: **text** → <b>text</b>
  result = result.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');

  // Italic: *text* → <i>text</i>
  result = result.replace(/\*(.+?)\*/g, '<i>$1</i>');

  // Strikethrough: ~~text~~ → <s>text</s>
  result = result.replace(/~~(.+?)~~/g, '<s>$1</s>');

  // Horizontal rules: --- → (empty, no equivalent)
  result = result.replace(/^[-*_]{3,}$/gm, '');

  // Restore code blocks
  result = result.replace(/\x00CODE(\d+)\x00/g, (_match, idx) => codeBlocks[Number(idx)]);

  return result as TelegramHtml;
}

// ---------------------------------------------------------------------------
// Plain-text helpers — escape free-form text into channel-native format.
// Use these for content that is NOT markdown: hard-coded UI strings with
// interpolated user/admin/agent data, system messages, channel forwarders.
// ---------------------------------------------------------------------------

/**
 * Escape free-form text for Telegram HTML mode.
 * Required for ANY interpolation that reaches `sendTelegramMessage`, because
 * Telegram parses `<`, `>`, `&`, `"` as HTML control characters when
 * `parse_mode: 'HTML'` is set.
 */
export function telegramPlain(text: string): TelegramHtml {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;') as TelegramHtml;
}

/**
 * Escape free-form text for Email HTML mode.
 * Mirrors the inline escape that previously lived in `sendEmailMessage`.
 */
export function emailPlain(text: string): EmailHtml {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>') as EmailHtml;
}

// Note: WhatsApp and Instagram have no `*Plain` helpers because neither has a
// reliable escape mechanism — WhatsApp still parses native markup (`*bold*`,
// `_italic_`, `~strike~`, `` `code` ``) inside any text, and Instagram has
// no formatting. Callers that need to pass plain text to these channels use
// `asWhatsAppTextUnsafe` / `asInstagramTextUnsafe` and own the visual quirks.

// ---------------------------------------------------------------------------
// Escape hatches — text the caller asserts is channel-native (no further
// escaping). Used for:
//   - the `agent.auto_format = false` opt-out, where the agent emits
//     pre-formatted channel-native output
//   - WhatsApp / Instagram, which have no robust plain-text escape
// Grep for `Unsafe` to audit every callsite.
// ---------------------------------------------------------------------------

export function asTelegramHtmlUnsafe(text: string): TelegramHtml { return text as TelegramHtml; }
export function asEmailHtmlUnsafe(text: string): EmailHtml { return text as EmailHtml; }
export function asWhatsAppTextUnsafe(text: string): WhatsAppText { return text as WhatsAppText; }
export function asInstagramTextUnsafe(text: string): InstagramText { return text as InstagramText; }

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

export function formatForChannel(channelType: 'whatsapp', text: string): WhatsAppText;
export function formatForChannel(channelType: 'instagram', text: string): InstagramText;
export function formatForChannel(channelType: 'email', text: string): EmailHtml;
export function formatForChannel(channelType: 'telegram', text: string): TelegramHtml;
export function formatForChannel(channelType: 'app' | 'system', text: string): AppText | SystemText;
export function formatForChannel(channelType: string, text: string): ChannelFormatted;
export function formatForChannel(channelType: string, text: string): ChannelFormatted {
  switch (channelType) {
    case 'whatsapp':
      return markdownToWhatsApp(text);
    case 'instagram':
      return markdownToPlainText(text);
    case 'email':
      return markdownToHtml(text);
    case 'telegram':
      return markdownToTelegramHtml(text);
    case 'system':
      return text as SystemText;
    case 'app':
    default:
      return text as AppText;
  }
}
